import React, { useMemo, useState } from "react";
import { useI18n } from "../i18n/I18nContext";
import { PLIS_TO_MM_LABEL, PLIS_RECONNUS, isRecettePlis } from "../utils/empilementPopulations";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LabelList,
} from "recharts";

const CLASS_ORDER = ["A", "B", "C", "D"];
const CLASS_RANK = { A: 0, B: 1, C: 2, D: 3 };
const CLASS_COLOR = {
  A: "#388e3c",
  B: "#a5d6a7",
  C: "#ef6c00",
  D: "#e53935",
};
const EP_LABELS = PLIS_TO_MM_LABEL;
const MONTHS_FR = [
  "janv.", "févr.", "mars", "avr.", "mai", "juin",
  "juil.", "août", "sept.", "oct.", "nov.", "déc.",
];

function normId(raw) {
  if (raw == null) return null;
  const matches = String(raw).match(/\d{5,}/g);
  if (!matches?.length) return null;
  return matches.reduce((a, b) => (a.length >= b.length ? a : b));
}

function stripAccents(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function normalizeCategorie(raw) {
  if (raw == null || raw === "") return null;
  const s = stripAccents(raw).trim().toUpperCase();
  if (CLASS_RANK[s] !== undefined) return s;
  const m = s.match(/[ABCD]/);
  return m ? m[0] : null;
}

function parseIlssDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  const iso = s.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(`${iso[1]}-${iso[2]}-${iso[3]}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const fr = s.match(/(\d{2})\/(\d{2})\/(\d{4})/);
  if (fr) {
    const d = new Date(`${fr[3]}-${fr[2]}-${fr[1]}T00:00:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isoWeek(d) {
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = (t.getUTCDay() + 6) % 7;
  t.setUTCDate(t.getUTCDate() - dayNum + 3);
  const isoYear = t.getUTCFullYear();
  const firstThu = new Date(Date.UTC(isoYear, 0, 4));
  const firstDayNum = (firstThu.getUTCDay() + 6) % 7;
  firstThu.setUTCDate(firstThu.getUTCDate() - firstDayNum + 3);
  const week = 1 + Math.round((t - firstThu) / (7 * 86400000));
  return { year: isoYear, week };
}

export function resolveIlssStacks(ilssItems) {
  const byStack = {};
  for (const it of ilssItems || []) {
    const sid = normId(it.ID_Stack);
    if (!sid) continue;
    if (!byStack[sid]) byStack[sid] = { cats: [], dates: [] };
    const cat = normalizeCategorie(it.Categorie);
    const dt = parseIlssDate(it.Date || it.Timestamp);
    if (cat) byStack[sid].cats.push(cat);
    if (dt) byStack[sid].dates.push(dt);
  }

  const records = [];
  for (const [stack, b] of Object.entries(byStack)) {
    if (!b.cats.length || !b.dates.length) continue;
    const worst = b.cats.reduce(
      (w, c) => (CLASS_RANK[c] > CLASS_RANK[w] ? c : w),
      b.cats[0]
    );
    const dRecent = new Date(Math.max(...b.dates.map((d) => d.getTime())));
    records.push({
      stack,
      cls: worst,
      date: dRecent.toISOString().slice(0, 10),
      n: b.cats.length,
    });
  }
  return records;
}

export function buildNbPlisMap(stacks) {
  const map = {};
  for (const s of stacks || []) {
    const sid = normId(s.IDStack);
    if (!sid) continue;
    const nb = Number(s.NbPlis);
    if (isRecettePlis(nb)) map[sid] = nb;
  }
  return map;
}

function buildChartData(records, gran) {
  const periods = {};
  const counts = {};

  for (const rec of records) {
    const d = new Date(`${rec.date}T00:00:00`);
    let meta;
    if (gran === "week") {
      const w = isoWeek(d);
      const sort = w.year * 100 + w.week;
      meta = { sort, year: w.year, week: w.week, kind: "week" };
    } else {
      const y = d.getFullYear();
      const m = d.getMonth() + 1;
      meta = { sort: y * 100 + m, year: y, month: m, kind: "month" };
    }
    periods[meta.sort] = meta;
    if (!counts[meta.sort]) counts[meta.sort] = { A: 0, B: 0, C: 0, D: 0 };
    counts[meta.sort][rec.cls] += 1;
  }

  const sortedKeys = Object.keys(periods).map(Number).sort((a, b) => a - b);
  const years = new Set(sortedKeys.map((k) => periods[k].year));
  const multiYear = years.size > 1;

  return sortedKeys.map((k) => {
    const p = periods[k];
    let label;
    if (p.kind === "week") {
      label = multiYear ? `S${p.week} ${p.year}` : `S${p.week}`;
    } else {
      label = `${MONTHS_FR[p.month - 1]} ${p.year}`;
    }
    return { label, ...counts[k] };
  });
}

function ColleTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={S.tooltip}>
      <div style={{ color: "#E6FF3E", fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.dataKey} style={{ color: CLASS_COLOR[p.dataKey], fontSize: 12 }}>
          Classe {p.dataKey} : <strong>{p.value}</strong> stack(s)
        </div>
      ))}
    </div>
  );
}

export default function IlssQualiteCollePanel({ ilssItems, stacks, loading }) {
  const { t, ui } = useI18n();

  const [gran, setGran] = useState("week");
  const [filterPlis, setFilterPlis] = useState("all");

  const nbPlisMap = useMemo(() => buildNbPlisMap(stacks), [stacks]);

  const allRecords = useMemo(() => resolveIlssStacks(ilssItems), [ilssItems]);

  const records = useMemo(() => {
    if (filterPlis === "all") return allRecords;
    const target = Number(filterPlis);
    return allRecords.filter((r) => nbPlisMap[r.stack] === target);
  }, [allRecords, filterPlis, nbPlisMap]);

  const chartData = useMemo(() => buildChartData(records, gran), [records, gran]);

  const repartition = useMemo(() => {
    const rep = { A: 0, B: 0, C: 0, D: 0 };
    records.forEach((r) => { rep[r.cls] += 1; });
    return rep;
  }, [records]);

  const plisCounts = useMemo(() => {
    const c = { other: 0, ...Object.fromEntries(PLIS_RECONNUS.map((n) => [n, 0])) };
    allRecords.forEach((r) => {
      const nb = nbPlisMap[r.stack];
      if (isRecettePlis(nb)) c[nb] += 1;
      else c.other += 1;
    });
    return c;
  }, [allRecords, nbPlisMap]);

  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div>
          <div style={S.cardTitle}>Qualité colle — catégories ILSS</div>
          <div style={S.cardSub}>
            1 stack = 1 barre groupée, classée dans sa <strong>pire</strong> catégorie (A &lt; B &lt; C &lt; D),
            rattachée à la date de test la plus récente.
            {records.length > 0 && (
              <span> — {records.length} stack(s) retenu(s)</span>
            )}
          </div>
        </div>
        <div style={S.controls}>
          <div style={S.filterGroup}>
            <span style={S.filterLabel}>Épaisseur :</span>
            {[
              { id: "all", label: "Tous" },
              ...PLIS_RECONNUS.map((n) => ({ id: String(n), label: PLIS_TO_MM_LABEL[n] })),
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                style={{ ...S.filterBtn, ...(filterPlis === opt.id ? S.filterBtnActive : {}) }}
                onClick={() => setFilterPlis(opt.id)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div style={S.filterGroup}>
            <span style={S.filterLabel}>Période :</span>
            <button
              type="button"
              style={{ ...S.filterBtn, ...(gran === "week" ? S.filterBtnActive : {}) }}
              onClick={() => setGran("week")}
            >
              Semaine
            </button>
            <button
              type="button"
              style={{ ...S.filterBtn, ...(gran === "month" ? S.filterBtnActive : {}) }}
              onClick={() => setGran("month")}
            >
              Mois
            </button>
          </div>
        </div>
      </div>

      <div style={S.kpiRow}>
        {CLASS_ORDER.map((cls) => (
          <div key={cls} style={{ ...S.kpiMini, borderColor: CLASS_COLOR[cls] }}>
            <div style={{ ...S.kpiMiniVal, color: CLASS_COLOR[cls] }}>{repartition[cls]}</div>
            <div style={S.kpiMiniLbl}>Classe {cls}</div>
          </div>
        ))}
        <div style={{ ...S.kpiMini, borderColor: "#64748b" }}>
          <div style={{ ...S.kpiMiniVal, color: "#94a3b8" }}>{plisCounts.other}</div>
          <div style={S.kpiMiniLbl}>Autres</div>
        </div>
      </div>

      {loading ? (
        <div style={S.empty}>Chargement ILSS…</div>
      ) : chartData.length === 0 ? (
        <div style={S.empty}>
          Aucune donnée ILSS pour ce filtre
          {filterPlis !== "all" ? ` (${EP_LABELS[Number(filterPlis)] || filterPlis})` : ""}.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={380}>
          <BarChart data={chartData} margin={{ top: 24, right: 24, left: 0, bottom: 8 }} barCategoryGap="28%">
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a50" />
            <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 11 }} />
            <YAxis
              allowDecimals={false}
              tick={{ fill: "#64748b", fontSize: 11 }}
              label={{ value: "Nombre de stacks", angle: -90, position: "insideLeft", fill: "#94a3b8", fontSize: 11 }}
            />
            <Tooltip content={<ColleTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, color: "#94a3b8" }} />
            {CLASS_ORDER.map((cls) => (
              <Bar key={cls} dataKey={cls} name={`Classe ${cls}`} fill={CLASS_COLOR[cls]} radius={[3, 3, 0, 0]}>
                <LabelList
                  dataKey={cls}
                  position="top"
                  formatter={(v) => (v > 0 ? v : "")}
                  style={{ fill: "#cbd5e1", fontSize: 11, fontWeight: 700 }}
                />
              </Bar>
            ))}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

const S = {
  card: {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid #1e3a50",
    borderRadius: 14,
    padding: "20px 22px",
    backdropFilter: "blur(8px)",
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: "#94a3b8",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    marginBottom: 6,
  },
  cardSub: { fontSize: 12, color: "#64748b", lineHeight: 1.5, maxWidth: 720 },
  controls: { display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" },
  filterGroup: { display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" },
  filterLabel: { fontSize: 12, color: "#94a3b8", marginRight: 4 },
  filterBtn: {
    padding: "6px 12px",
    borderRadius: 6,
    border: "1px solid #1e3a50",
    background: "rgba(255,255,255,0.04)",
    color: "#94a3b8",
    cursor: "pointer",
    fontWeight: 700,
    fontSize: 12,
  },
  filterBtnActive: { background: "#388e3c", color: "#fff", borderColor: "#388e3c" },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
    gap: 10,
    marginBottom: 16,
  },
  kpiMini: {
    border: "1px solid",
    borderRadius: 10,
    padding: "10px 8px",
    textAlign: "center",
    background: "rgba(255,255,255,0.02)",
  },
  kpiMiniVal: { fontSize: 22, fontWeight: 900 },
  kpiMiniLbl: { fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase" },
  empty: { textAlign: "center", padding: "48px 20px", color: "#64748b", fontSize: 13 },
  tooltip: {
    background: "#071e2f",
    border: "1px solid #1e3a50",
    borderRadius: 8,
    padding: "10px 14px",
  },
};
